import { ref } from 'vue'
import uniqid from 'uniqid'
import { defineStore } from 'pinia'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  title: string
  message: string
  variant: ToastVariant
  duration: number
}

export interface ToastOptions {
  title: string
  message?: string
  duration?: number
}

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([])

  function add(options: ToastOptions, variant: ToastVariant) {
    toasts.value.push({
      id: uniqid(),
      title: options.title,
      message: options.message ?? '',
      duration: options.duration ?? 3000,
      variant,
    })
  }

  function remove(id: string) {
    toasts.value = toasts.value.filter(t => t.id !== id)
  }

  return { toasts, add, remove }
})

function createToast(input: string | ToastOptions, variant: ToastVariant): string {
  const options = typeof input === 'string' ? { title: input } : input
  const id = uniqid()
  const store = useToastStore()

  store.toasts.push({
    id,
    title: options.title,
    message: options.message ?? '',
    duration: options.duration ?? 3000,
    variant,
  })

  return id
}

function dismissToast(id: string) {
  const store = useToastStore()
  const toast = store.toasts.find(t => t.id === id)
  if (toast) {
    // Trigges close animation after 1ms
    toast.duration = 1
  }
}

export const AuToast = {
  success: (input: string | ToastOptions) => createToast(input, 'success'),
  error: (input: string | ToastOptions) => createToast(input, 'error'),
  info: (input: string | ToastOptions) => createToast(input, 'info'),
  warning: (input: string | ToastOptions) => createToast(input, 'warning'),
  dismiss: (id: string) => dismissToast(id),
}
